import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import AppContext, { OBJECT_SEARCH, OBJECT_TYPE_POI } from '../../context/AppContext';
import { useMap } from 'react-leaflet';
import debounce from 'lodash-es/debounce';
import isEmpty from 'lodash-es/isEmpty';
import L from 'leaflet';
import { changeIconColor, createPoiIcon, DEFAULT_ICON_SIZE } from '../markers/MarkerOptions';
import { applySelectedPin, hideMarkersUnderPin } from '../util/MarkerSelectionService';
import 'leaflet-spin';
import PoiManager, {
    createPoiCache,
    DEFAULT_ICON_COLOR,
    DEFAULT_POI_COLOR,
    DEFAULT_POI_SHAPE,
    navigateToPoi,
    updatePoiCache,
} from '../../manager/PoiManager';
import 'leaflet.markercluster';
import { apiGet, apiPost } from '../../util/HttpApi';
import { CATEGORY_TYPE, FINAL_POI_ICON_NAME, POI_ID, POI_NAME } from '../../infoblock/components/wpt/WptTagsProvider';
import { getVisibleBboxInfo, mapSpinOptionsForVisibleBbox } from './MapStateLayer';
import { createMapObject, createSearchObjectOptions, getObjIdSearch } from '../../manager/SearchManager';
import { SEARCH_ICON_MAP_LOCATION, searchTypeMap } from '../../manager/searchConstants';
import i18n from '../../i18n';
import { getApiLang, getWikiLangs } from '../../util/lang';
import { clusterMarkers, addMarkerTooltip, createSecondaryMarker } from '../util/Clusterizer';
import { escapeHtmlAttr, useSelectMarkerOnMap } from '../../util/hooks/map/useSelectMarkerOnMap';
import {
    BBOX_COORDS_DECIMALS,
    MENU_INFO_OPEN_SIZE,
    NAVIGATE_URL,
    POI_LAYER_ID,
    SEARCH_LAYER_ID,
    showProcessingNotification,
} from '../../manager/GlobalManager';
import { NAVIGATION_OBJECT_TYPE_SEARCH } from '../../manager/NavigationManager';
import useZoomMoveMapHandlers from '../../util/hooks/map/useZoomMoveMapHandlers';
import { findFeatureGroupById, getIconFromMap, panToIfNeeded } from '../util/MapManager';
import { EXPLORE_OBJS_KEY, POI_OBJECTS_KEY, useRecentDataSaver } from '../../util/hooks/menu/useRecentDataSaver';
import { useNavigate } from 'react-router-dom';
import LoginContext from '../../context/LoginContext';
import { INIT_LOGIN_STATE } from '../../manager/LoginManager';
import { getPoiApi } from '../../manager/SearchApi';

const SPINNER_DELAY_MS = 500;
const GET_POI_DEBOUNCE_MS = 500;

// WARNING: Do not use the 'title' field in marker layers on the map.
// See the 'parseWpt' function for more details.
export async function createPoiLayer({ ctx, poiList = [], globalPoiIconCache, type = OBJECT_TYPE_POI, map, zoom }) {
    const innerCache = await createPoiCache({ poiList, poiIconCache: globalPoiIconCache });
    updatePoiCache(ctx, innerCache);

    const center = map.getCenter();
    const latitude = center.lat;
    const { mainMarkers, secondaryMarkers } = clusterMarkers({
        places: poiList,
        zoom,
        latitude,
        isPoi: true,
    });

    const mainMarkersLayers = await Promise.all(
        mainMarkers?.map(async (poi) => {
            const finalIconName = poi.properties[FINAL_POI_ICON_NAME] ?? PoiManager.getFinalPoiIconName(poi.properties);
            const icon = await getPoiIcon(poi, innerCache, finalIconName);
            const coord = poi.geometry.coordinates;
            const marker = new L.Marker(new L.LatLng(coord[1], coord[0]), {
                ...poi.properties,
                idObj: getObjIdSearch(poi),
                name: poi.properties[POI_NAME],
                icon,
                svg: icon.options.svg,
                [FINAL_POI_ICON_NAME]: finalIconName,
            });

            marker.on('add', function () {
                marker.getElement().setAttribute('se-poi-name', poi.properties[POI_NAME]);
            });

            return marker;
        })
    );

    let simpleMarkersArr = new L.FeatureGroup();

    for (const place of secondaryMarkers) {
        const circle = createSecondaryMarker(place);
        if (circle) {
            simpleMarkersArr.addLayer(circle);
        }
    }

    mainMarkersLayers.forEach((marker) => {
        addMarkerTooltip({
            marker,
            setSelectedId: ctx.setSelectedWptId,
            mainStyle: true,
            text: marker.options[POI_NAME],
            latlng: marker._latlng,
            iconSize: DEFAULT_ICON_SIZE,
            map,
            ctx,
            type: POI_LAYER_ID,
        });
    });

    simpleMarkersArr.getLayers().forEach((marker) => {
        addMarkerTooltip({
            marker,
            setSelectedId: ctx.setSelectedWptId,
            text: marker.options[POI_NAME],
            latlng: marker._latlng,
            map,
            ctx,
            type: POI_LAYER_ID,
        });
    });

    const layers = [...mainMarkersLayers, ...simpleMarkersArr.getLayers()];

    if (layers.length) {
        return L.featureGroup(layers, {
            id: type === OBJECT_SEARCH ? SEARCH_LAYER_ID : POI_LAYER_ID,
        });
    } else {
        return L.featureGroup(); // return an empty layer group if there are no layers
    }
}

export async function getPoiIcon(poi, cache, finalIconName) {
    if (finalIconName) {
        const svgData = cache[finalIconName] ?? (await getIconFromMap(finalIconName));
        if (svgData) {
            let coloredSvg = changeIconColor(svgData, DEFAULT_ICON_COLOR);
            // Add the id attribute to the coloredSvg
            const poiName = escapeHtmlAttr(poi.properties[POI_NAME]);
            coloredSvg = coloredSvg.replace(
                '<svg',
                `<svg id="se-poi-marker-icon-${finalIconName}-${DEFAULT_ICON_COLOR}-${poiName}"`
            );
            const iconHtml = createPoiIcon({
                color: DEFAULT_POI_COLOR,
                background: DEFAULT_POI_SHAPE,
                svgIcon: coloredSvg,
            }).options.html;
            return L.divIcon({
                html: iconHtml,
                svg: coloredSvg,
                iconSize: [DEFAULT_ICON_SIZE, DEFAULT_ICON_SIZE],
                iconAnchor: [DEFAULT_ICON_SIZE / 2, DEFAULT_ICON_SIZE / 2],
            });
        }
    }
}

export default function PoiLayer() {
    const ctx = useContext(AppContext);
    const ltx = useContext(LoginContext);
    const map = useMap();

    // for fixing dead layers
    const reqIdRef = useRef(0);

    const navigate = useNavigate();

    const [prevZoom, setPrevZoom] = useState(null);
    const [zoom, setZoom] = useState(map ? map.getZoom() : 0);
    const [move, setMove] = useState(false);
    const [poiList, setPoiList] = useState({
        layer: null,
        listFeatures: null,
    });

    const [prevController, setPrevController] = useState(false);
    const [prevCategories, setPrevCategories] = useState(null);
    const [categoryNames, setCategoryNames] = useState(null);
    useZoomMoveMapHandlers(map, setZoom, setMove);
    const recentSaver = useRecentDataSaver();

    const getPoiLayers = useCallback(() => {
        const mainLayers = poiList?.layer?.getLayers() ?? [];
        return mainLayers.length > 0 ? mainLayers : null;
    }, [poiList?.layer]);

    useSelectMarkerOnMap({
        ctx,
        getLayers: getPoiLayers,
        type: POI_LAYER_ID,
        map,
        zoom,
        move,
    });

    useEffect(() => {
        // the login check closes an open object menu, so open the object only after it
        if (ltx.loginUser === INIT_LOGIN_STATE) {
            return;
        }
        if (ctx.poiByUrl?.params) {
            openPoiByUrl()
                .then(async (res) => {
                    let poiLayer;
                    if (res) {
                        if (ctx.selectedWptId?.id !== getObjIdSearch(res)) {
                            poiLayer = await createPoiLayer({
                                ctx,
                                poiList: [res],
                                globalPoiIconCache: ctx.poiIconCache,
                                map,
                                zoom,
                            });
                            if (ctx.poiByUrl.layer) {
                                map.removeLayer(ctx.poiByUrl.layer);
                            }
                            map.addLayer(poiLayer);

                            const markers = poiLayer.getLayers();
                            if (markers.length > 0) {
                                const marker = markers[0];
                                applySelectedPin({
                                    ctx,
                                    map,
                                    layer: marker,
                                    markerData: {
                                        color: DEFAULT_POI_COLOR,
                                        background: DEFAULT_POI_SHAPE,
                                        iconHtml: marker.options.svg ?? '',
                                    },
                                    isSelection: true,
                                });
                                panToIfNeeded({ map, latlng: marker.getLatLng(), ctx });
                            }
                        }
                    }
                    ctx.setPoiByUrl({
                        params: null,
                        layer: poiLayer,
                        open: true,
                    });
                })
                .finally(() => {
                    ctx.setProcessingPoiByUrl(false);
                });
        } else if (ctx.poiByUrl?.layer && !ctx.poiByUrl?.open) {
            map.removeLayer(ctx.poiByUrl.layer);
            ctx.setPoiByUrl(null);
            ctx.setProcessingPoiByUrl(false);
        }
    }, [ctx.poiByUrl, ltx.loginUser]);

    useEffect(() => {
        ctx.setShowPoiCategories((prev) => {
            const pois = ctx.configureMapState?.pois;
            if (!pois || !Array.isArray(pois)) {
                return prev;
            }

            const configCats = new Set(pois.map((x) => x.category));
            // drop config-origin items that no longer exist in Configure Map
            const kept = prev.filter((o) => !(o.fromConfig && !configCats.has(o.category)));

            const prevConfigCats = new Set(kept.filter((o) => o.fromConfig).map((o) => o.category));

            const toAdd = pois.filter((o) => !prevConfigCats.has(o.category)).map((o) => ({ ...o, fromConfig: true }));

            return toAdd.length || kept.length !== prev.length ? [...kept, ...toAdd] : prev;
        });
    }, [ctx.configureMapState.pois]);

    async function openPoiByUrl() {
        const { pin, name, type, osmId, wikidataId } = ctx.poiByUrl.params;

        const response = type
            ? await getPoiApi({ pin, name, type, osmId, wikidataId, lang: getWikiLangs(i18n.language) })
            : null;
        if (response?.data) {
            const data = response.data;

            const objId = data.properties[POI_ID] ?? `${data.geometry.coordinates[1]},${data.geometry.coordinates[0]}`;
            if (objId != null) {
                data.properties[POI_ID] = objId;
            }

            data.properties[FINAL_POI_ICON_NAME] = PoiManager.getFinalPoiIconName(data.properties);
            // if it has poiTags, then it has info from both wiki and osm
            const poiTags = data.properties.poiTags;

            if (wikidataId || poiTags) {
                // open wiki poi
                const key = data.properties?.osmid ?? data.geometry.coordinates[1] + data.geometry.coordinates[0];
                const wiki = data ?? null;
                const poi = poiTags
                    ? {
                          properties: { ...poiTags },
                          latlng: {
                              lat: wiki.geometry.coordinates[1],
                              lng: wiki.geometry.coordinates[0],
                          },
                          mapObj: true,
                      }
                    : null;

                const obj = { poi, wikidata: wiki, key, mapObj: true, id: poi?.options?.idObj };
                ctx.setSelectedWpt(obj);
                recentSaver(EXPLORE_OBJS_KEY, obj);
            } else {
                // open normal poi
                ctx.setCurrentObjectType(OBJECT_TYPE_POI);
                const poi = createMapObject(data);
                ctx.setSelectedWpt({ poi, id: getObjIdSearch(data) });
                recentSaver(POI_OBJECTS_KEY, poi);
            }

            ctx.setInfoBlockWidth(MENU_INFO_OPEN_SIZE + 'px');
            return data;
        } else if (pin) {
            // show coordinates menu if get-poi returned nothing
            const coords = pin.split(',');
            if (coords.length === 2) {
                const lat = Number.parseFloat(coords[0]);
                const lng = Number.parseFloat(coords[1]);
                if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
                    ctx.setCurrentObjectType(OBJECT_TYPE_POI);
                    const poi = {
                        key: `${lng}-${lat}`,
                        options: createSearchObjectOptions(
                            searchTypeMap.LOCATION,
                            `${lat.toFixed(5)}, ${lng.toFixed(5)}`
                        ),
                        latlng: { lat, lng },
                        mapObj: true,
                    };
                    ctx.setSelectedWpt({ poi, id: `${lat},${lng}` });
                    ctx.setInfoBlockWidth(MENU_INFO_OPEN_SIZE + 'px');
                }
                return {
                    properties: {
                        [FINAL_POI_ICON_NAME]: SEARCH_ICON_MAP_LOCATION,
                    },
                    geometry: {
                        type: 'Point',
                        coordinates: [lng, lat],
                    },
                };
            }
            return null;
        } else {
            return null;
        }
    }

    async function getPoi(controller, showPoiCategories, visibleBboxInfo) {
        if (!showPoiCategories || showPoiCategories.length === 0) {
            return null;
        }
        const { bounds, center } = visibleBboxInfo;
        const searchData = {
            categories: showPoiCategories,
            northWest: `${Number(bounds.getNorthWest().lat).toFixed(BBOX_COORDS_DECIMALS)},${Number(bounds.getNorthWest().lng).toFixed(BBOX_COORDS_DECIMALS)}`,
            southEast: `${Number(bounds.getSouthEast().lat).toFixed(BBOX_COORDS_DECIMALS)},${Number(bounds.getSouthEast().lng).toFixed(BBOX_COORDS_DECIMALS)}`,
        };
        const response = await apiPost(`${process.env.REACT_APP_ROUTING_API_SITE}/search/search-poi`, searchData, {
            params: {
                locale: getApiLang(i18n.language),
                lat: center.lat,
                lon: center.lng,
                zoom: Math.round(map.getZoom()),
            },
            apiCache: true,
            signal: controller.signal,
        });
        if (response?.data) {
            return response.data;
        } else {
            return null;
        }
    }

    function typesChanged() {
        if (!isEmpty(ctx.showPoiCategories)) {
            if (ctx.searchQuery?.type) {
                // always clear the old poi list
                return true;
            }
            if (!prevCategories) {
                return true;
            }
            // compare previous and current categories
            const prev = prevCategories.map((c) => c.category).sort();
            const current = ctx.showPoiCategories.map((c) => c.category).sort();

            if (JSON.stringify(prev) !== JSON.stringify(current)) {
                return true;
            }
        }
        return false;
    }

    useEffect(() => {
        let ignore = false;
        let controller = new AbortController();

        function clearPoiList() {
            updateLayerOnMap(null);
            setPoiList(null);
        }

        function updateLayerOnMap(poiListState) {
            const layer = poiListState?.layer;
            const hasMarkers = layer?.getLayers?.()?.length > 0;
            const existing = findFeatureGroupById(map, POI_LAYER_ID);
            if (!hasMarkers) {
                if (existing) {
                    map.removeLayer(existing);
                }
                return;
            }
            if (map.hasLayer(layer)) {
                hideMarkersUnderPin(map, ctx);
                return;
            }
            if (existing) {
                map.removeLayer(existing);
            }
            layer.addTo(map);
            layer.on('click', onClick);
            hideMarkersUnderPin(map, ctx);
        }

        const getPoiTask = async ({
            controller,
            poiList,
            showPoiCategories,
            poiIconCache,
            zoom,
            reqId,
            visibleBboxInfo,
        }) => {
            if (!visibleBboxInfo) {
                return;
            }
            const spinTimeout = setTimeout(
                () => map.spin(true, mapSpinOptionsForVisibleBbox(map, ctx)),
                SPINNER_DELAY_MS
            );
            const notifyTimeout = showProcessingNotification(ctx);
            try {
                const res = await getPoi(controller, showPoiCategories, visibleBboxInfo);
                if (reqId !== reqIdRef.current || ignore) return;
                setCategoryNames(res?.categoryNames ?? null);
                if (res?.zoomInHint) {
                    // category too common for this zoom
                    updateLayerOnMap(null);
                    setPoiList({ layer: null, listFeatures: null, info: res.info, tooMany: true });
                    setPrevCategories(showPoiCategories);
                } else if (res) {
                    let features = null;
                    let listFeatures = null;

                    if (res.features?.features?.length > 0) {
                        features = res.features.features;
                        listFeatures = res.features;
                    }

                    if (features?.length && listFeatures) {
                        const newLayer = await createPoiLayer({
                            ctx,
                            poiList: features,
                            globalPoiIconCache: poiIconCache,
                            map,
                            zoom,
                        });
                        if (reqId !== reqIdRef.current || ignore) return;
                        const nextState = { layer: newLayer, listFeatures, info: res.info ?? poiList?.info };
                        updateLayerOnMap(nextState);
                        setPoiList(nextState);
                        setPrevCategories(showPoiCategories);
                    } else {
                        clearPoiList();
                    }
                } else {
                    clearPoiList();
                }
            } catch (e) {
                if (e?.name !== 'AbortError') throw e;
            } finally {
                clearTimeout(spinTimeout);
                map.spin(false);
                clearTimeout(notifyTimeout);
                ctx.setProcessingSearch(false);
            }
        };
        const debouncedGetPoi = debounce(getPoiTask, GET_POI_DEBOUNCE_MS);

        hideMarkersUnderPin(map, ctx);

        async function getPoiList() {
            if (isEmpty(ctx.showPoiCategories)) {
                // if categories are cleared, then clear the list and related states
                reqIdRef.current += 1;
                setPrevCategories(null);
                if (poiList) {
                    clearPoiList();
                }
                return;
            }
            const isTypeChange = typesChanged();
            const categoriesChanged =
                !prevCategories ||
                JSON.stringify(prevCategories.map((c) => c.category).sort()) !==
                    JSON.stringify(ctx.showPoiCategories.map((c) => c.category).sort());
            if (zoom !== prevZoom || move || isTypeChange) {
                if (prevController) {
                    prevController.abort();
                }
                setPrevController(controller);
                setPrevZoom(zoom);
                if (categoriesChanged) {
                    setPrevCategories(null);
                }
                reqIdRef.current += 1;
                const runGetPoi = categoriesChanged || isTypeChange ? getPoiTask : debouncedGetPoi;
                runGetPoi({
                    controller,
                    poiList: categoriesChanged ? null : poiList,
                    showPoiCategories: ctx.showPoiCategories,
                    poiIconCache: ctx.poiIconCache,
                    zoom,
                    reqId: reqIdRef.current,
                    visibleBboxInfo: getVisibleBboxInfo(ctx, map),
                });
            } else if (poiList?.listFeatures?.features?.length > 0) {
                // same categories, no zoom/move change: re-render the existing pois without a new request
                const newLayer = await createPoiLayer({
                    ctx,
                    poiList: poiList.listFeatures.features,
                    globalPoiIconCache: ctx.poiIconCache,
                    map,
                    zoom,
                });
                const nextState = {
                    layer: newLayer,
                    listFeatures: poiList?.listFeatures,
                    info: poiList?.info,
                };
                updateLayerOnMap(nextState);
                setPoiList(nextState);
            }
        }

        if (ltx.isLoggedIn()) {
            getPoiList().then();
        }
        return () => {
            ignore = true;
            controller.abort?.();
            debouncedGetPoi.cancel();
        };
    }, [zoom, move, ctx.showPoiCategories]);

    useEffect(() => {
        if (!ctx.processingSearch) {
            addToSearchRes(poiList);
        }
    }, [poiList, ctx.processingSearch]);

    useEffect(() => {
        return () => {
            const existing = findFeatureGroupById(map, POI_LAYER_ID);
            if (existing) {
                map.removeLayer(existing);
            }
            prevController?.abort?.();
        };
    }, []);

    function addToSearchRes(poiList) {
        if (ctx.searchQuery?.type) {
            ctx.setSearchResult((prevResult) => {
                return {
                    ...prevResult,
                    features: poiList && !poiList.tooMany ? poiList.listFeatures?.features : [],
                    info: poiList?.info,
                    tooMany: poiList?.tooMany ?? false,
                    categoryNames,
                };
            });
        }
    }

    function onClick(e) {
        if (globalThis.location.pathname.includes(NAVIGATE_URL)) {
            const lat = e.latlng?.lat ?? e.sourceTarget?._latlng?.lat;
            const lon = e.latlng?.lng ?? e.sourceTarget?._latlng?.lng;

            if (lat && lon) {
                const wpt = {
                    ...e.sourceTarget.options,
                    lat,
                    lng: lon,
                    type: NAVIGATION_OBJECT_TYPE_SEARCH,
                };
                const navEvent = new CustomEvent('nav-marker-click', {
                    detail: {
                        wpt,
                        latlng: L.latLng(lat, lon),
                    },
                    cancelable: true,
                });
                navEvent.originalEvent = e.originalEvent;
                globalThis.dispatchEvent(navEvent);

                // If NavigationLayer handled it (found empty input), don't open info
                if (navEvent.defaultPrevented) {
                    L.DomEvent.stopPropagation(e);
                    return;
                }
            }
        }

        // Normal logic: open info block
        ctx.setCurrentObjectType(OBJECT_TYPE_POI);
        ctx.setInfoBlockWidth(MENU_INFO_OPEN_SIZE + 'px');

        const poi = {
            mapObj: true,
            options: e.sourceTarget.options,
            latlng: e.sourceTarget._latlng,
        };
        recentSaver(POI_OBJECTS_KEY, poi);
        ctx.setSelectedWpt({ poi, id: e.sourceTarget.options?.idObj });
        if (poi.options[CATEGORY_TYPE] === searchTypeMap.POI) {
            navigateToPoi({ poi }, navigate);
        }
    }

    return null;
}
